export const CHAT_CONTEXT_DRAG_MIME = "application/x-zeqi-chat-context";
export const CHAT_CONTEXT_PICK_MODE_EVENT = "zeqi:chat-context-pick-mode";

const normalizeText = (value) => String(value || "").trim();
let activeDraggedChatContext = null;

export function sanitizeChatContextItem(context = {}) {
  const key = normalizeText(context?.key);
  if (!key) return null;

  return {
    key,
    label: normalizeText(context?.label) || "Context",
    type: normalizeText(context?.type) || "context",
    reportId: normalizeText(context?.reportId) || null,
    sessionId: normalizeText(context?.sessionId) || null,
    collectionName: normalizeText(context?.collectionName) || null,
    source: normalizeText(context?.source) || null,
    sourceLabel: normalizeText(context?.sourceLabel) || null,
    markdown: typeof context?.markdown === "string" ? context.markdown : null,
    fileName: normalizeText(context?.fileName) || null,
    product: context?.product && typeof context.product === "object" ? context.product : null,
    window: context?.window && typeof context.window === "object" ? context.window : null,
    shopifyProductId: normalizeText(context?.shopifyProductId) || null,
    productId: normalizeText(context?.productId) || null,
    itemId: normalizeText(context?.itemId) || null,
    itemName: normalizeText(context?.itemName) || null,
    windowKey: normalizeText(context?.windowKey) || null,
    windowLabel: normalizeText(context?.windowLabel) || null,
  };
}

export function sanitizeChatContext(context = {}) {
  const rawItems = Array.isArray(context?.items)
    ? context.items
    : Array.isArray(context)
      ? context
      : [context];
  const items = rawItems.map(sanitizeChatContextItem).filter(Boolean);
  const dedupedItems = Array.from(
    new Map(items.map((item) => [item.key, item])).values()
  );
  if (!dedupedItems.length) return null;

  const reportIds = Array.from(
    new Set(
      dedupedItems
        .map((item) => normalizeText(item.reportId))
        .filter(Boolean)
    )
  );
  const activeKey =
    normalizeText(context?.activeKey) || dedupedItems.at(-1)?.key || null;
  const activeItem =
    dedupedItems.find((item) => item.key === activeKey) ||
    dedupedItems.at(-1) ||
    null;

  return {
    mode: reportIds.length > 1 ? "multi" : "single",
    activeKey,
    items: dedupedItems,
    reportIds,
    reportId: reportIds.at(-1) || null,
    key: activeItem?.key || activeKey || "main",
    label: activeItem?.label || "Context",
    type: activeItem?.type || "context",
    collectionName: activeItem?.collectionName || null,
    sessionId: activeItem?.sessionId || null,
    source: activeItem?.source || null,
    sourceLabel: activeItem?.sourceLabel || null,
    markdown: activeItem?.markdown || null,
    fileName: activeItem?.fileName || null,
    product: activeItem?.product || null,
    window: activeItem?.window || null,
    shopifyProductId: activeItem?.shopifyProductId || null,
    productId: activeItem?.productId || null,
    itemId: activeItem?.itemId || null,
    itemName: activeItem?.itemName || null,
    windowKey: activeItem?.windowKey || null,
    windowLabel: activeItem?.windowLabel || null,
  };
}

export function writeDraggedChatContext(event, context = {}) {
  const payload = sanitizeChatContext(context);
  if (!payload || !event?.dataTransfer) return false;

  const encoded = JSON.stringify(payload);
  activeDraggedChatContext = payload;
  event.dataTransfer.effectAllowed = "copy";
  event.dataTransfer.setData(CHAT_CONTEXT_DRAG_MIME, encoded);
  event.dataTransfer.setData("text/plain", encoded);
  return true;
}

export function readDraggedChatContext(event) {
  const encoded =
    event?.dataTransfer?.getData?.(CHAT_CONTEXT_DRAG_MIME) ||
    event?.dataTransfer?.getData?.("text/plain") ||
    "";
  if (!encoded) return sanitizeChatContext(activeDraggedChatContext);
  try {
    return sanitizeChatContext(JSON.parse(encoded)) || sanitizeChatContext(activeDraggedChatContext);
  } catch {
    return sanitizeChatContext(activeDraggedChatContext);
  }
}

export function setChatContextPickMode(enabled) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent(CHAT_CONTEXT_PICK_MODE_EVENT, {
      detail: { enabled: Boolean(enabled) },
    })
  );
}
